import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Globe, Instagram, Twitch, Twitter, Youtube } from "lucide-react";
import { useEffect } from "react";
import { DriverAchievements } from "@/components/drivers/driver-achievements";
import { DriverStats } from "@/components/drivers/driver-stats";
import { JsonLd } from "@/components/json-ld";
import { SetupCard } from "@/components/setups/setup-card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useDriverProfile } from "@/hooks/use-drivers";
import { analytics } from "@/lib/analytics";
import { getDriverProfile } from "@/lib/drivers";
import type { SetupListItem } from "@/lib/types";
import { canonicalLink, seo } from "@/utils/seo";
import { breadcrumbJsonLd, driverPersonJsonLd } from "@/utils/structured-data";

export const Route = createFileRoute("/drivers/$driverId")({
  loader: async ({ params }) => {
    try {
      const driver = await getDriverProfile({ data: { driverId: params.driverId } });
      return {
        driverId: params.driverId,
        driverName: driver?.display_name,
        driverAvatar: driver?.avatar_url,
        driverBio: driver?.bio,
      };
    } catch {
      return { driverId: params.driverId, driverName: null, driverAvatar: null, driverBio: null };
    }
  },
  head: ({ loaderData }) => {
    const name = loaderData?.driverName;
    const avatar = loaderData?.driverAvatar;
    const bio = loaderData?.driverBio;
    const title = name ? `${name} | Setups by SZESE` : "Driver Profile | Setups by SZESE";
    const description =
      bio ||
      (name
        ? `View setups and achievements for ${name}, SZESE E-Sport sim racer.`
        : "View setups and achievements for this SZESE E-Sport driver.");
    const canonicalUrl = `https://setupsbyszese.com/drivers/${loaderData?.driverId}`;
    return {
      meta: [
        ...seo({
          title,
          description,
          image: avatar || undefined,
          canonical: canonicalUrl,
        }),
      ],
      links: [canonicalLink(canonicalUrl)],
    };
  },
  component: DriverProfilePage,
});

const SOCIAL_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  website: Globe,
  twitter: Twitter,
  x: Twitter,
  instagram: Instagram,
  youtube: Youtube,
  twitch: Twitch,
};

function getSocialIcon(key: string) {
  const normalized = key.toLowerCase();
  return SOCIAL_ICONS[normalized] ?? Globe;
}

function getSocialLabel(key: string) {
  const labels: Record<string, string> = {
    website: "Website",
    twitter: "Twitter / X",
    x: "Twitter / X",
    instagram: "Instagram",
    youtube: "YouTube",
    twitch: "Twitch",
  };
  return labels[key.toLowerCase()] ?? key;
}

function DriverProfileSkeleton() {
  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-5xl px-4 py-8">
        <div className="h-6 w-40 rounded bg-muted animate-pulse" />
        <div className="mt-12 flex flex-col items-center gap-6">
          <div className="h-32 w-32 rounded-full bg-muted animate-pulse" />
          <div className="h-8 w-48 rounded bg-muted animate-pulse" />
          <div className="h-4 w-64 rounded bg-muted animate-pulse" />
          <div className="flex gap-2">
            <div className="h-6 w-20 rounded bg-muted animate-pulse" />
            <div className="h-6 w-20 rounded bg-muted animate-pulse" />
          </div>
        </div>
        <div className="mt-12 grid grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 rounded-xl bg-muted animate-pulse" />
          ))}
        </div>
      </div>
    </div>
  );
}

function DriverProfilePage() {
  const { driverId } = Route.useParams();
  const { data: driver, isLoading, isError } = useDriverProfile(driverId);

  // Analytics
  useEffect(() => {
    if (driverId) {
      analytics.driverProfileViewed({ driverId });
    }
  }, [driverId]);

  // Update page title when driver data loads
  useEffect(() => {
    if (driver?.display_name) {
      document.title = `${driver.display_name} | Setups by SZESE`;
    }
  }, [driver?.display_name]);

  if (isLoading) {
    return <DriverProfileSkeleton />;
  }

  if (isError || !driver) {
    return (
      <main className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <h1 className="font-display text-3xl font-bold uppercase text-foreground">
          Driver Not Found
        </h1>
        <p className="text-muted-foreground">
          The driver profile you are looking for does not exist.
        </p>
        <Link
          to="/app/drivers"
          className="text-primary hover:text-primary/80 hover:underline transition-colors"
        >
          Back to Drivers
        </Link>
      </main>
    );
  }

  const initials = driver.display_name
    .split(" ")
    .map((n: string) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const socialEntries = Object.entries(driver.social_links ?? {}).filter(([, url]) => url);

  return (
    <main className="min-h-screen bg-background">
      <JsonLd
        data={driverPersonJsonLd({
          name: driver.display_name,
          url: `https://setupsbyszese.com/drivers/${driverId}`,
          image: driver.avatar_url || undefined,
          description: driver.bio || undefined,
          socialLinks: driver.social_links ?? undefined,
        })}
      />
      <JsonLd
        data={breadcrumbJsonLd([
          { name: "Home", url: "https://setupsbyszese.com" },
          { name: "Drivers", url: "https://setupsbyszese.com/app/drivers" },
          { name: driver.display_name, url: `https://setupsbyszese.com/drivers/${driverId}` },
        ])}
      />
      <div className="mx-auto max-w-5xl px-4 py-8">
        {/* Back link */}
        <Link
          to="/app/drivers"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Drivers
        </Link>

        {/* Hero header */}
        <section className="relative mt-8 rounded-2xl border border-border/50 bg-card/30 overflow-hidden">
          {/* Background: subtle Kalocsai full pattern */}
          <div className="pointer-events-none absolute inset-0">
            <img
              src="/kalocsai-full.webp"
              alt=""
              aria-hidden="true"
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover"
              style={{ opacity: 0.06 }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/40 to-background/80" />
          </div>

          {/* Kalocsai pattern corners */}
          <img
            src="/kalocsai-corner-tl.webp"
            alt=""
            aria-hidden="true"
            loading="lazy"
            className="pointer-events-none absolute top-0 left-0 z-[1] w-[200px] h-[200px] object-contain"
            style={{
              opacity: 0.18,
              maskImage: "radial-gradient(ellipse at top left, black 20%, transparent 70%)",
              WebkitMaskImage: "radial-gradient(ellipse at top left, black 20%, transparent 70%)",
            }}
          />
          <img
            src="/kalocsai-corner-tr.webp"
            alt=""
            aria-hidden="true"
            loading="lazy"
            className="pointer-events-none absolute top-0 right-0 z-[1] w-[200px] h-[200px] object-contain"
            style={{
              opacity: 0.18,
              maskImage: "radial-gradient(ellipse at top right, black 20%, transparent 70%)",
              WebkitMaskImage: "radial-gradient(ellipse at top right, black 20%, transparent 70%)",
            }}
          />
          <img
            src="/kalocsai-corner-bl.webp"
            alt=""
            aria-hidden="true"
            loading="lazy"
            className="pointer-events-none absolute bottom-0 left-0 z-[1] w-[160px] h-[160px] object-contain"
            style={{
              opacity: 0.14,
              maskImage: "radial-gradient(ellipse at bottom left, black 20%, transparent 70%)",
              WebkitMaskImage:
                "radial-gradient(ellipse at bottom left, black 20%, transparent 70%)",
            }}
          />

          <img
            src="/kalocsai-corner-br.webp"
            alt=""
            aria-hidden="true"
            loading="lazy"
            className="pointer-events-none absolute bottom-0 right-0 z-[1] w-[160px] h-[160px] object-contain"
            style={{
              opacity: 0.14,
              maskImage: "radial-gradient(ellipse at bottom right, black 20%, transparent 70%)",
              WebkitMaskImage:
                "radial-gradient(ellipse at bottom right, black 20%, transparent 70%)",
            }}
          />

          {/* Helmet background */}
          <img
            src="/helmet-bg-solid.webp"
            alt=""
            aria-hidden="true"
            loading="lazy"
            className="pointer-events-none absolute -right-10 -bottom-10 z-[2] h-[360px] w-[360px] object-cover sm:h-[420px] sm:w-[420px]"
            style={{
              opacity: 0.12,
              maskImage: "radial-gradient(ellipse at 70% 60%, black 20%, transparent 70%)",
              WebkitMaskImage: "radial-gradient(ellipse at 70% 60%, black 20%, transparent 70%)",
            }}
          />

          {/* Gradient orbs */}
          <div className="pointer-events-none absolute -top-20 -right-20 h-[300px] w-[300px] rounded-full bg-[#6B9FD4]/5 blur-[80px]" />
          <div className="pointer-events-none absolute -bottom-20 -left-20 h-[250px] w-[250px] rounded-full bg-[#D94030]/4 blur-[80px]" />

          <div className="relative z-10 flex flex-col md:flex-row md:min-h-[360px]">
            {/* Driver photo — left side */}
            {driver.avatar_url ? (
              <div className="relative h-64 w-full md:h-auto md:w-[40%] shrink-0">
                <img
                  src={driver.avatar_url}
                  alt={driver.display_name}
                  referrerPolicy="no-referrer"
                  width={480}
                  height={360}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            ) : null}

            {/* Text content — right side */}
            <div className="flex flex-1 flex-col justify-center px-8 py-8 md:py-12">
              {!driver.avatar_url && (
                <Avatar className="mb-4 h-24 w-24 self-start border-4 border-border">
                  <AvatarFallback className="bg-muted text-muted-foreground text-3xl font-bold">
                    {initials}
                  </AvatarFallback>
                </Avatar>
              )}

              <h1
                className="text-4xl font-bold uppercase tracking-tight text-foreground md:text-5xl"
                style={{ fontFamily: "'Bebas Neue', sans-serif" }}
              >
                {driver.display_name}
              </h1>

              {driver.bio && (
                <p className="mt-3 max-w-md text-sm text-muted-foreground leading-relaxed">
                  {driver.bio}
                </p>
              )}

              <div className="mt-4 flex flex-wrap items-center gap-2">
                {driver.irating_road != null && (
                  <Badge variant="secondary" className="gap-1 font-mono text-xs">
                    {driver.irating_road.toLocaleString()} iR Road
                  </Badge>
                )}
                {driver.irating_oval != null && driver.irating_oval > 2000 && (
                  <Badge variant="secondary" className="gap-1 font-mono text-xs">
                    {driver.irating_oval.toLocaleString()} iR Oval
                  </Badge>
                )}
                {driver.license_road && (
                  <Badge variant="outline" className="text-xs">
                    {driver.license_road}
                  </Badge>
                )}
              </div>

              {socialEntries.length > 0 && (
                <div className="mt-5 flex items-center gap-3">
                  {socialEntries.map(([key, url]) => {
                    const Icon = getSocialIcon(key);
                    return (
                      <a
                        key={key}
                        href={url as string}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={getSocialLabel(key)}
                        className="flex h-9 w-9 items-center justify-center rounded-full border border-border/50 bg-card/50 text-muted-foreground transition-colors hover:border-folk-blue/30 hover:text-folk-blue"
                      >
                        <Icon className="h-4 w-4" />
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Stats bar */}
        <section className="mt-8">
          <DriverStats
            totalSetups={driver.stats.total_setups}
            totalDownloads={driver.stats.total_downloads}
            activeSeries={driver.stats.active_series}
          />
        </section>

        {/* Achievements */}
        {driver.achievements && driver.achievements.length > 0 && (
          <section className="mt-8">
            <DriverAchievements achievements={driver.achievements} />
          </section>
        )}

        {/* Setup list */}
        <section className="mt-8">
          <h2 className="font-display text-xl font-bold uppercase tracking-tight text-foreground">
            Setups
          </h2>
          {driver.setups.length > 0 ? (
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {driver.setups.map((setup: SetupListItem) => (
                <SetupCard key={setup.id} setup={setup} />
              ))}
            </div>
          ) : (
            <p className="mt-4 text-muted-foreground">No setups available yet.</p>
          )}
        </section>
      </div>
    </main>
  );
}
